import React, { useState, useEffect } from 'react';
import { Calendar, UserCheck, Clock, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const appraisers = ['Tyler Brodsky', 'Mike Johnson', 'Sarah Chen', 'Tom Rodriguez'];

const commonTimes = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
  '17:00', '17:30', '18:00'
];

/**
 * Schedule Appointment Modal Component
 * Modal for scheduling appointments for customers
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.customer - Customer object to schedule appointment for
 * @param {Function} props.onSuccess - Callback when appointment is successfully scheduled
 * @param {Object} props.theme - Theme object with styling properties
 * @param {Object} props.colors - Color palette object
 */
export const ScheduleAppointmentModal = ({
  show,
  onClose,
  customer,
  onSuccess,
  theme,
  colors
}) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('14:00');
  const [appraiser, setAppraiser] = useState('Tyler Brodsky');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when customer changes
  useEffect(() => {
    if (customer && show) {
      // Set default date to today
      const today = new Date().toISOString().split('T')[0];
      setDate(today);
      setTime(customer.appointmentTime || '14:00');
      setAppraiser(customer.assignedAppraiser || 'Tyler Brodsky');
      setNotes(customer.appointmentNotes || '');
      setError(null);
    }
  }, [customer, show]);

  if (!show || !customer) return null;

  const customerName = customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim();

  const handleSave = async () => {
    if (!date || !time || !appraiser) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    // Log appointment data being sent (raw values before formatting)
    console.log('Appointment data being sent (raw):', {
      date: date,
      time: time,
      dateType: typeof date,
      timeType: typeof time,
      appraiser: appraiser,
      customerId: customer?.id
    });

    try {
      // Format date to YYYY-MM-DD format
      // Ensure we get only the date part (split on 'T' to remove time if present)
      let formattedDate = String(date).trim();
      
      // If date includes time (from ISO string), split it off
      if (formattedDate.includes('T')) {
        formattedDate = formattedDate.split('T')[0];
      }
      
      // Validate YYYY-MM-DD format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formattedDate)) {
        // If not in correct format, try to parse and reformat
        const dateObj = new Date(formattedDate);
        if (isNaN(dateObj.getTime())) {
          throw new Error('Invalid date format. Please use YYYY-MM-DD format.');
        }
        // Reformat to YYYY-MM-DD
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      }

      // Format time to HH:MM format (24-hour, with leading zeros)
      // Remove any whitespace
      let formattedTime = String(time).trim().replace(/\s+/g, '');
      
      // If time doesn't have a colon, it's invalid
      if (!formattedTime.includes(':')) {
        throw new Error('Invalid time format. Please use HH:MM format (e.g., 14:00).');
      }
      
      // Split time into hours and minutes
      const [hours, minutes] = formattedTime.split(':');
      
      // Pad hours to 2 digits (e.g., "9" -> "09")
      const paddedHours = String(hours).padStart(2, '0');
      
      // Ensure minutes are 2 digits (e.g., "5" -> "05")
      const paddedMinutes = String(minutes || '0').padStart(2, '0');
      
      // Reconstruct time in HH:MM format
      formattedTime = `${paddedHours}:${paddedMinutes}`;
      
      // Validate HH:MM format (24-hour format, e.g., "14:00")
      const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(formattedTime)) {
        throw new Error('Invalid time format. Please use HH:MM format (e.g., 14:00).');
      }

      // Final validation
      if (!formattedDate || !formattedTime) {
        throw new Error('Date and time are required.');
      }

      // Log the exact formatted values being sent to help debug
      const requestBody = {
        date: formattedDate,
        time: formattedTime,
        appraiser,
        notes: notes || ''
      };
      
      console.log('Submitting appointment (formatted):', { 
        ...requestBody,
        formattedDate,
        formattedTime,
        formattedDateType: typeof formattedDate,
        formattedTimeType: typeof formattedTime,
        customerId: customer.id,
        requestBodyString: JSON.stringify(requestBody)
      });

      // Sending appointment data
      let response;
      let data;
      
      try {
        response = await fetch(`${API_URL}/api/customers/${customer.id}/schedule-appointment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        console.log('Response status:', response.status, response.statusText);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
        
        // Try to parse response as JSON
        const responseText = await response.text();
        console.log('Response text (raw):', responseText);
        
        try {
          data = JSON.parse(responseText);
          console.log('Response data (parsed):', data);
        } catch (parseError) {
          console.error('Failed to parse response as JSON:', parseError);
          console.error('Response text was:', responseText);
          throw new Error(`Server returned invalid JSON. Status: ${response.status}. Response: ${responseText.substring(0, 200)}`);
        }

        if (!response.ok) {
          console.error('Appointment error - Response not OK:', {
            status: response.status,
            statusText: response.statusText,
            data: data,
            message: data?.message || data?.error || 'Failed to schedule appointment',
            fullResponse: data
          });
          throw new Error(data?.message || data?.error || `Server error: ${response.status} ${response.statusText}`);
        }

        if (data.success) {
          // Call success callback to refresh customer list
          if (onSuccess) {
            onSuccess(data.customer);
          }
          onClose();
        } else {
          throw new Error(data.message || data.error || 'Failed to schedule appointment');
        }
      } catch (fetchError) {
        // Re-throw to be caught by outer catch block
        throw fetchError;
      }
    } catch (err) {
      console.error('Error scheduling appointment:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
        errorString: String(err),
        errorJSON: JSON.stringify(err, Object.getOwnPropertyNames(err))
      });
      
      // Log the full error object
      console.error('Full error object:', err);
      
      setError(err.message || 'Failed to schedule appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      animation: 'fadeIn 0.3s ease-out'
    }}
    onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}
    >
      <div style={{
        position: 'relative',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 20%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 80%, rgba(255, 255, 255, 0.1) 100%)',
        backdropFilter: 'blur(40px) saturate(150%)',
        WebkitBackdropFilter: 'blur(40px) saturate(150%)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '24px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
        padding: '40px',
        width: '90%',
        maxWidth: '600px',
        animation: 'scaleIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        {/* Top highlight */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)'
        }} />
        
        {/* Inner glow */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.08), transparent 50%)',
          pointerEvents: 'none'
        }} />
        
        <div style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          paddingBottom: '24px'
        }}>
          <h2 style={{
            color: '#ffffff',
            margin: 0,
            fontSize: '28px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
            textShadow: '0 0 40px rgba(255, 255, 255, 0.1)'
          }}>
            <Calendar size={24} color="#3b82f6" />
            Schedule Appointment
          </h2>
          <button
            onClick={onClose}
            style={{
              width: '40px',
              height: '40px',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
            }}
          >
            ×
          </button>
        </div>

        {/* Customer Name (Read-only) */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            color: theme.textSecondary,
            marginBottom: '8px',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            Customer
          </label>
          <div style={{
            padding: '12px 16px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            color: theme.text,
            fontSize: '15px',
            fontWeight: '600'
          }}>
            {customerName}
          </div>
        </div>

        {/* Date Picker */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: theme.textSecondary,
            marginBottom: '8px',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            <Calendar size={14} color={colors.primary} />
            Appointment Date *
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            style={{
              width: '100%',
              padding: '16px 20px',
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '14px',
              color: '#fff',
              fontSize: '15px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              outline: 'none',
              transition: 'all 0.3s ease',
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              e.target.style.background = 'rgba(59, 130, 246, 0.05)';
              e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1), 0 0 30px rgba(59, 130, 246, 0.2), inset 0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              e.target.style.background = 'rgba(255, 255, 255, 0.03)';
              e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.2)';
            }}
          />
        </div>

        {/* Time Picker */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: theme.textSecondary,
            marginBottom: '8px',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            <Clock size={14} color={colors.primary} />
            Appointment Time *
          </label>
          <select
            value={time}
            onChange={(e) => setTime(e.target.value)}
            style={{
              width: '100%',
              padding: '16px 20px',
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '14px',
              color: '#fff',
              fontSize: '15px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              outline: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              e.target.style.background = 'rgba(59, 130, 246, 0.05)';
              e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1), 0 0 30px rgba(59, 130, 246, 0.2), inset 0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              e.target.style.background = 'rgba(255, 255, 255, 0.03)';
              e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.2)';
            }}
          >
            {commonTimes.map(timeOption => (
              <option key={timeOption} value={timeOption} style={{ background: theme.cardBg, color: theme.text }}>
                {new Date(`2000-01-01T${timeOption}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
              </option>
            ))}
          </select>
        </div>

        {/* Appraiser Dropdown */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: theme.textSecondary,
            marginBottom: '8px',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            <UserCheck size={14} color={colors.primary} />
            Assign to Appraiser *
          </label>
          <select
            value={appraiser}
            onChange={(e) => setAppraiser(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: theme.text,
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => {
              e.target.style.border = `1px solid ${colors.primary}`;
              e.target.style.background = 'rgba(255, 255, 255, 0.08)';
            }}
            onBlur={(e) => {
              e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)';
              e.target.style.background = 'rgba(255, 255, 255, 0.05)';
            }}
          >
            {appraisers.map(app => (
              <option key={app} value={app} style={{ background: theme.cardBg, color: theme.text }}>
                {app}
              </option>
            ))}
          </select>
        </div>

        {/* Notes Textarea */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: theme.textSecondary,
            marginBottom: '8px',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            <FileText size={14} color={colors.primary} />
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this appointment..."
            rows={4}
            style={{
              width: '100%',
              padding: '16px 20px',
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '14px',
              color: '#fff',
              fontSize: '15px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              outline: 'none',
              resize: 'vertical',
              transition: 'all 0.3s ease',
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              e.target.style.background = 'rgba(59, 130, 246, 0.05)';
              e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1), 0 0 30px rgba(59, 130, 246, 0.2), inset 0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              e.target.style.background = 'rgba(255, 255, 255, 0.03)';
              e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.2)';
            }}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            color: '#ef4444',
            fontSize: '13px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginTop: '24px',
          borderTop: '1px solid rgba(102, 126, 234, 0.2)',
          paddingTop: '20px'
        }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              position: 'relative',
              zIndex: 1,
              flex: 1,
              padding: '14px 24px',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '14px',
              color: '#fff',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              letterSpacing: '0.5px',
              transition: 'all 0.3s ease',
              opacity: loading ? 0.5 : 1,
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !date || !time || !appraiser}
            style={{
              position: 'relative',
              zIndex: 1,
              flex: 1,
              padding: '14px 28px',
              background: loading || !date || !time || !appraiser
                ? 'rgba(59, 130, 246, 0.5)'
                : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
              border: 'none',
              borderRadius: '14px',
              color: '#fff',
              cursor: loading || !date || !time || !appraiser ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              letterSpacing: '0.5px',
              transition: 'all 0.3s ease',
              opacity: loading || !date || !time || !appraiser ? 0.6 : 1,
              boxShadow: loading || !date || !time || !appraiser
                ? 'none'
                : '0 0 20px rgba(59, 130, 246, 0.5), 0 4px 15px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (!loading && date && time && appraiser) {
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 0 40px rgba(59, 130, 246, 0.6), 0 8px 25px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = loading || !date || !time || !appraiser
                ? 'none'
                : '0 0 20px rgba(59, 130, 246, 0.5), 0 4px 15px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
            }}
            onMouseDown={(e) => {
              if (!loading && date && time && appraiser) {
                e.currentTarget.style.transform = 'translateY(0) scale(0.98)';
              }
            }}
          >
            {loading ? 'Saving...' : 'Schedule Appointment'}
          </button>
        </div>
      </div>
    </div>
  );
};

